import { useState } from "react";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { ConfirmDialog } from "@/components/common/Dialog";
import { deletePipeline } from "@/api/pipelineMaintenance/api";
import { showSuccessToast } from "@/hooks/use-toast";
import { Pipeline } from "@/types/domain";
import { LoadingOverlay } from "@/components/common/Loading/LoadingOverlay";

export type DeletePipelineDialogProps = {
  pipeline: Pipeline;
  callBack: () => Promise<void>;
}

export const DeletePipelineDialog = ({ pipeline, callBack }: DeletePipelineDialogProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  let triggerTitle = "Delete this pipeline";
  const allowedToDelete = !pipeline.inUse;
  if (!allowedToDelete) {
    triggerTitle = `Cannot delete: This pipeline has stages assigned to it.`;
  }

  const handleOpenChange = (open: boolean) => {
    setDialogOpen(open);
    setError(null);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    setError(null);
    try {
      await deletePipeline(pipeline.id);
      showSuccessToast("Pipeline deleted successfully.");
      setDialogOpen(false);
      await callBack();
    } catch (err) {
      setError("Failed to delete pipeline.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LoadingOverlay 
        isVisible={loading} 
        message="Deleting pipeline..." 
      />
      <ConfirmDialog
        open={dialogOpen}
        onOpenChange={handleOpenChange}
        triggerIcon={faTrashCan}
        triggerText=""
        triggerVariant="icon"
        disableTrigger={allowedToDelete === false}
        triggerTitle={triggerTitle}
        title="Delete Pipeline"
        description={
          <>
            Are you sure you want to delete <strong>{pipeline.name}</strong>?<br />
            This action cannot be undone.
            {error && (
              <div className="text-red-500 text-sm mt-2">{error}</div>
            )}
          </>
        }
        confirmText="Delete"
        cancelText="Cancel"
        loading={loading}
        onConfirm={handleConfirmDelete}
        disableConfirm={loading}
      />
    </>
  );
}